import React, { useState } from 'react';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { Bars3Icon, LockClosedIcon } from '@heroicons/react/20/solid';
import { classNames } from '../utils/classNames';

// The columns of a table, one line each, in their order, under the label of their group ("Today", "Previous year"): a
// checkbox shows or hides a column, and a handle moves it to a new place inside its group (a drag with the pointer; with
// the keyboard, Arrow up and Arrow down on the focused handle). The handle of a group label moves the whole group before
// or after another group, so that the header row of the table keeps each group together. A locked column (the first
// one) has no checkbox and no handle and keeps its place. The caller owns the list: onChange receives the new list
// (order and visible). With onReset, a text button gives back the order of the page.
export type ColumnsPanelItem = {
	id: string,
	label: string,
	visible: boolean,
	// The label of the group of the column. The columns of one group follow each other in the list.
	group?: string,
	// The first column: always shown, never moved.
	locked?: boolean,
};

type Props = {
	items: ColumnsPanelItem[],
	onChange: (items: ColumnsPanelItem[]) => void,
	// The name of the list for a screen reader ("Columns of the night table").
	ariaLabel: string,
	onReset?: () => void,
	resetLabel?: string,
	// A line under the list, for example "Saved in your account".
	note?: string,
};

const GROUP = 'group:';
const groupOf = (item: ColumnsPanelItem) => item.group ?? '';

// The list as its groups, in order: [group label, its items].
const groupsOf = (items: ColumnsPanelItem[]): [string, ColumnsPanelItem[]][] => {
	const out: [string, ColumnsPanelItem[]][] = [];
	items.forEach(item => {
		const last = out[out.length - 1];
		if (last !== undefined && last[0] === groupOf(item) && item.locked !== true && last[1][0].locked !== true) last[1].push(item);
		else out.push([groupOf(item), [item]]);
	});
	return out;
};

const moveItem = (items: ColumnsPanelItem[], from: number, to: number): ColumnsPanelItem[] => {
	const next = [...items];
	const [item] = next.splice(from, 1);
	next.splice(to, 0, item);
	return next;
};

// Moves the group at index `from` (in the list of groups) to the place of the group at `to`.
const moveGroup = (items: ColumnsPanelItem[], from: number, to: number): ColumnsPanelItem[] => {
	const groups = groupsOf(items);
	if (groups[from] === undefined || groups[to] === undefined || groups[to][1][0].locked === true) return items;
	const [group] = groups.splice(from, 1);
	groups.splice(to, 0, group);
	return groups.flatMap(([, list]) => list);
};

const HANDLE = 'cursor-grab touch-none rounded text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600';

const Handle: React.FunctionComponent<{ id: string, label: string, onStep: (by: number) => void }> = ({ id, label, onStep }) => {
	const drag = useDraggable({ id });
	return <button type="button" ref={drag.setNodeRef} {...drag.listeners} {...drag.attributes} aria-label={`Move ${label}: Arrow up or Arrow down`}
		onKeyDown={event => {
			if (event.key === 'ArrowUp') { event.preventDefault(); onStep(-1); }
			if (event.key === 'ArrowDown') { event.preventDefault(); onStep(1); }
		}} className={HANDLE}>
		<Bars3Icon aria-hidden="true" className="h-4 w-4" />
	</button>;
};

const Drop: React.FunctionComponent<React.PropsWithChildren<{ id: string, active: boolean, className: string }>> = ({ id, active, className, children }) => {
	const drop = useDroppable({ id });
	return <li ref={drop.setNodeRef} className={classNames(className, drop.isOver && active ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : '')}>{children}</li>;
};

const ColumnsPanel: React.FunctionComponent<Props> = ({ items, onChange, ariaLabel, onReset, resetLabel = 'Reset to the default', note }) => {
	const [active, setActive] = useState<string | null>(null);
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
	const groups = groupsOf(items);
	const indexOf = (id: string) => items.findIndex(i => i.id === id);
	const groupIndexOf = (label: string) => groups.findIndex(([g, list]) => g === label && list[0].locked !== true);
	// A column moves only inside its group; a group moves only onto another group label.
	const canDrop = (from: string, to: string): boolean => {
		if (from.startsWith(GROUP)) return to.startsWith(GROUP) && from !== to;
		if (to.startsWith(GROUP)) return false;
		const [a, b] = [items[indexOf(from)], items[indexOf(to)]];
		return a !== undefined && b !== undefined && b.locked !== true && groupOf(a) === groupOf(b);
	};
	const onDragStart = (event: DragStartEvent) => setActive(String(event.active.id));
	const onDragEnd = (event: DragEndEvent) => {
		setActive(null);
		if (event.over === null) return;
		const [from, to] = [String(event.active.id), String(event.over.id)];
		if (!canDrop(from, to)) return;
		if (from.startsWith(GROUP)) onChange(moveGroup(items, groupIndexOf(from.slice(GROUP.length)), groupIndexOf(to.slice(GROUP.length))));
		else onChange(moveItem(items, indexOf(from), indexOf(to)));
	};
	const stepItem = (item: ColumnsPanelItem, by: number) => {
		const index = indexOf(item.id), next = items[index + by];
		if (next !== undefined && next.locked !== true && groupOf(next) === groupOf(item)) onChange(moveItem(items, index, index + by));
	};
	const stepGroup = (label: string, by: number) => {
		const index = groupIndexOf(label), next = groups[index + by];
		if (next !== undefined && next[1][0].locked !== true) onChange(moveGroup(items, index, index + by));
	};
	const dragLabel = active === null ? undefined : active.startsWith(GROUP) ? active.slice(GROUP.length) : items[indexOf(active)]?.label;
	return <div className="flex flex-col gap-3">
		<DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActive(null)}>
			<ul aria-label={ariaLabel} className="flex flex-col">
				{groups.map(([label, list], g) => <React.Fragment key={`${label}-${list[0].id}`}>
					{label !== '' && list[0].locked !== true && <Drop id={`${GROUP}${label}`} active={active !== null && canDrop(active, `${GROUP}${label}`)}
						className={classNames('flex items-center gap-x-3 rounded-md px-2 pb-1', g > 0 ? 'pt-3' : '')}>
						<Handle id={`${GROUP}${label}`} label={`the group ${label}`} onStep={by => stepGroup(label, by)} />
						<span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
					</Drop>}
					{list.map(item => <Drop key={item.id} id={item.id} active={active !== null && canDrop(active, item.id)}
						className={classNames('flex items-center gap-x-3 rounded-md px-2 py-1.5', label !== '' && item.locked !== true ? 'pl-6' : '', item.id === active ? 'opacity-40' : '')}>
						{item.locked === true
							? <LockClosedIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-300" />
							: <Handle id={item.id} label={item.label} onStep={by => stepItem(item, by)} />}
						<label className={classNames('flex min-w-0 flex-1 items-center gap-x-2 text-sm', item.locked === true ? 'text-gray-500' : 'cursor-pointer text-gray-900')}>
							<input type="checkbox" checked={item.visible} disabled={item.locked === true}
								onChange={() => onChange(items.map(i => i.id === item.id ? { ...i, visible: !i.visible } : i))}
								className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600 disabled:opacity-50" />
							<span className="truncate">{item.label}</span>
						</label>
					</Drop>)}
				</React.Fragment>)}
			</ul>
			<DragOverlay>{dragLabel === undefined ? null
				: <div className="flex items-center gap-x-3 rounded-md bg-white px-2 py-1.5 text-sm text-gray-900 shadow-lg ring-1 ring-gray-900/10">
					<Bars3Icon aria-hidden="true" className="h-4 w-4 text-gray-500" />{dragLabel}
				</div>}</DragOverlay>
		</DndContext>
		{(onReset !== undefined || note !== undefined) && <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-2 pt-3">
			{note !== undefined && <span className="text-xs text-gray-500">{note}</span>}
			{onReset !== undefined && <button type="button" onClick={onReset}
				className="rounded text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">{resetLabel}</button>}
		</div>}
	</div>;
};

export default ColumnsPanel;
